// SPDX-License-Identifier: AGPL-3.0-only

/**
 * The API client.
 *
 * # EVERY FAILURE IS A PROBLEM DOCUMENT
 *
 * The server renders validation, authentication, authorization and domain
 * failures as RFC 9457 `application/problem+json` (server/internal/api/problem.go).
 * So the client parses ONE error shape, and every error it raises carries the
 * request id — which is the only thing that joins what a user saw to what the
 * server logged.
 *
 * # WHAT THIS CLIENT DELIBERATELY DOES NOT DO
 *
 * It does not store a token. The session lives in an `HttpOnly` cookie the
 * browser attaches automatically and JavaScript cannot read — which is what
 * makes an XSS unable to exfiltrate it. There is therefore nothing here to keep
 * in `localStorage`, and adding it would undo that property.
 *
 * The CSRF token IS held in memory, because it must be echoed in a header that
 * a cross-site page cannot set. Memory only: putting it in a readable cookie
 * would recreate the double-submit weakness the server-side binding avoids.
 */

/** An RFC 9457 problem document. */
export interface Problem {
  type: string;
  title: string;
  status: number;
  detail?: string;
  /** The request id. Quote this when reporting a failure. */
  instance?: string;
  /** Per-field validation messages. Never contains submitted values. */
  errors?: Record<string, string>;
}

/**
 * An API failure.
 *
 * Carries the parsed problem document so a caller can branch on `status` or
 * render `errors` per field, and exposes `requestId` because that is what an
 * operator will ask for.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly problem: Problem;

  constructor(problem: Problem) {
    super(problem.detail ?? problem.title);
    this.name = 'ApiError';
    this.status = problem.status;
    this.problem = problem;
  }

  /** The request id, for support and for correlating with server logs. */
  get requestId(): string | undefined {
    return this.problem.instance;
  }

  /** Field-level validation messages, when the failure was a 422. */
  get fieldErrors(): Record<string, string> {
    return this.problem.errors ?? {};
  }
}

/** Header the server expects the CSRF token in. Must match auth.CSRFHeader. */
const CSRF_HEADER = 'X-Meshvara-CSRF';

/**
 * The CSRF token for the current session, held in memory only.
 *
 * Set at login and cleared at logout. A page reload loses it, which is correct:
 * the session cookie survives, so `GET` requests still work, and the first
 * state-changing request after a reload needs the token re-established — see
 * `restoreSession` in the auth context.
 */
let csrfToken: string | null = null;

export function setCsrfToken(token: string | null): void {
  csrfToken = token;
}

/** Methods that are safe by definition and need no CSRF token. */
const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

interface RequestOptions {
  method?: string;
  body?: unknown;
  signal?: AbortSignal;
}

/**
 * Make an API request.
 *
 * Throws `ApiError` for any non-2xx response, so callers handle one error type
 * and never have to remember to check `response.ok`.
 */
export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? 'GET';
  const headers: Record<string, string> = { Accept: 'application/json' };

  if (options.body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }
  if (!SAFE_METHODS.has(method) && csrfToken) {
    headers[CSRF_HEADER] = csrfToken;
  }

  let response: Response;
  try {
    response = await fetch(path, {
      method,
      headers,
      // The session cookie must be sent. It is HttpOnly, so this is the only
      // way it travels — the client cannot read or attach it manually.
      credentials: 'same-origin',
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
      signal: options.signal,
    });
  } catch (cause) {
    // A network failure is not a problem document, but callers should still get
    // one error type. Status 0 distinguishes "never reached the server".
    throw new ApiError({
      type: 'about:blank',
      title: 'Network error',
      status: 0,
      detail: 'The server could not be reached. Check your connection and try again.',
    });
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const isProblem = (response.headers.get('Content-Type') ?? '').includes('application/problem+json');

  if (!response.ok) {
    if (isProblem) {
      const problem = (await response.json()) as Problem;
      throw new ApiError(problem);
    }
    // A non-2xx that is NOT a problem document means something upstream of the
    // application answered — a proxy, a load balancer. Reported as such rather
    // than pretending the server said it.
    throw new ApiError({
      type: 'about:blank',
      title: 'Unexpected response',
      status: response.status,
      detail: `The server returned ${response.status} without a problem document.`,
      instance: response.headers.get('X-Request-Id') ?? undefined,
    });
  }

  return (await response.json()) as T;
}

/**
 * Fetch a file and hand it to the browser's downloader.
 *
 * # WHY THIS IS A fetch AND NOT AN ANCHOR TAG
 *
 * A plain `<a href="/api/v1/.../export">` would work — the session cookie is
 * HttpOnly and same-origin, so the browser attaches it either way. What it
 * would NOT do is fail well: a 404 or a 500 would navigate the tab away from
 * the application and leave the operator looking at a raw problem document with
 * no way back except the browser's Back button.
 *
 * Fetching it means a refusal arrives as an `ApiError` like every other refusal
 * in this application, and the page the operator was reading stays on screen.
 *
 * # WHERE THE FILENAME COMES FROM
 *
 * The Server's `Content-Disposition`, which it builds from ids and a timestamp
 * it chose — never from a device-supplied name. This function does not invent
 * one and does not accept one from its caller, so there is no path by which a
 * name a switch printed could become a name on somebody's disk.
 */
export async function download(path: string): Promise<void> {
  let response: Response;
  try {
    response = await fetch(path, {
      method: 'GET',
      headers: { Accept: 'application/zip' },
      credentials: 'same-origin',
    });
  } catch {
    throw new ApiError({
      type: 'about:blank',
      title: 'Network error',
      status: 0,
      detail: 'The server could not be reached. Check your connection and try again.',
    });
  }

  if (!response.ok) {
    const isProblem = (response.headers.get('Content-Type') ?? '').includes(
      'application/problem+json',
    );
    if (isProblem) {
      throw new ApiError((await response.json()) as Problem);
    }
    throw new ApiError({
      type: 'about:blank',
      title: 'Download failed',
      status: response.status,
      detail: `The server returned ${response.status} without a problem document.`,
      instance: response.headers.get('X-Request-Id') ?? undefined,
    });
  }

  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  try {
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = filenameFrom(response.headers.get('Content-Disposition'));
    // Not appended to the document: the click is dispatched on a detached
    // element, so nothing is ever inserted into the page that a later render
    // could leave behind.
    link.click();
  } finally {
    // RELEASED, ALWAYS. An object URL holds the whole blob in memory until it is
    // revoked, and an export can be tens of megabytes.
    URL.revokeObjectURL(objectUrl);
  }
}

/**
 * Read the filename out of a Content-Disposition header.
 *
 * # WHY THE NAME IS ACCEPTED OR REPLACED, NEVER REPAIRED
 *
 * `link.download` is handed straight to the browser's downloader, and a name
 * containing a path separator or a `..` is how a download escapes the folder it
 * was meant to land in. The obvious defence is to substitute the dangerous
 * characters — and that is the defence that goes wrong, because a substitution
 * can leave the dangerous shape behind: `../../etc/passwd` with the separators
 * replaced is still a string containing `..`, and reasoning about which
 * rewrites are safe is a much harder question than it looks.
 *
 * So there is no repair. A name that is not already plainly safe — a leading
 * alphanumeric, then only letters, digits, dot, dash and underscore, and no
 * `..` anywhere — is discarded entirely and a fixed name is used instead. The
 * cost is a fallback name on a header we could have salvaged; the benefit is
 * that there is no rewrite to get subtly wrong.
 *
 * # WHY THIS EXISTS AT ALL WHEN THE SERVER BUILDS THE NAME
 *
 * Because this function cannot see who built it. Today the Server composes it
 * from a UUID and a timestamp. A future route, or a proxy in front of one,
 * could put something else there, and this is the last point before the value
 * becomes a path on somebody's disk.
 */
function filenameFrom(header: string | null): string {
  const fallback = 'meshvara-export.zip';
  if (!header) return fallback;

  const captured = /filename="?([^";]+)"?/i.exec(header)?.[1]?.trim();
  if (!captured) return fallback;
  if (captured.includes('..')) return fallback;
  if (!/^[A-Za-z0-9][A-Za-z0-9._-]*$/.test(captured)) return fallback;
  // A bound, because a filesystem has one and a several-kilobyte name is not a
  // name anybody meant.
  if (captured.length > 128) return fallback;

  return captured;
}

// ---------------------------------------------------------------------------
// Typed endpoints
// ---------------------------------------------------------------------------

export interface Organization {
  id: string;
  installation_id: string;
  name: string;
}

export interface Company {
  id: string;
  organization_id: string;
  name: string;
}

export interface Site {
  id: string;
  organization_id: string;
  company_id: string;
  name: string;
}

export interface SessionInfo {
  user_id: string;
  organization_id: string;
  csrf_token: string;
  expires_at: string;
}

export interface LoginResult {
  user_id: string;
  organization_id: string;
  csrf_token: string;
  expires_at: string;
}

interface List<T> {
  items: T[];
}

/** A Gateway as an operator picks it from a list. */
export interface GatewaySummary {
  id: string;
  company_id: string;
  site_id: string;
  name: string;
  state: 'active' | 'revoked';
}

/**
 * Enrolment-token METADATA. There is no plaintext field and there cannot be one.
 *
 * The server stores a SHA-256 digest, so no read path could return the secret
 * even if somebody asked it to. The digest is not exposed either — publishing it
 * would let a holder confirm a guess offline, and nothing here needs it.
 */
export interface EnrolmentToken {
  id: string;
  company_id: string;
  site_id: string;
  state: 'unused' | 'consumed' | 'revoked';
  expires_at: string;
  created_at: string;
  created_by: string;
  consumed_at: string | null;
}

/**
 * What issuing a token returns: the metadata, and the plaintext ONCE.
 *
 * `enrolment_token` is the only time this value exists outside the operator's
 * hands. Whatever holds it must keep it in memory and nowhere else — see
 * GatewayEnrolment, and the tests in enrolment.hygiene.test.tsx that assert it
 * reaches no storage, no URL and no log.
 */
export interface IssuedEnrolmentToken {
  token: EnrolmentToken;
  enrolment_token: string;
  warning: string;
  /**
   * The installation this Gateway will belong to, and the PUBLIC key it will
   * pin before trusting any connection.
   *
   * Both travel with the token because a Gateway must hold the key BEFORE it
   * authenticates — learning it from the connection it is authenticating would
   * be trust on first use, which ADR-010 rejects.
   */
  installation_id: string;
  installation_public_key: string;

  /**
   * The ONE value an operator carries to a Gateway's setup page.
   *
   * It packs the endpoint, the installation id, the installation PUBLIC key and
   * the enrolment secret into a single string, so the Gateway pins the key
   * before it trusts any connection — out of band, in the operator's hands,
   * exactly as ADR-010 §2 requires.
   *
   * A SECRET, with every rule the plaintext token has: shown once, never
   * persisted, never in a URL, never logged.
   */
  setup_token?: string;

  /** Present instead of setup_token when the Server could not compose one. */
  setup_token_error?: string;
}

/**
 * Which Gateway build belongs with this Server.
 *
 * `recorded: false` means this Server was built without recording it. The page
 * says so rather than offering a plausible default — the operator has no way to
 * tell a guess from a fact, and a wrong guess means running the wrong software.
 */
export interface GatewayArtifact {
  recorded: boolean;
  image?: string;
  tag?: string;
  digest?: string;
  platform?: string;
  version?: string;
  pinned_reference?: string;
  tagged_reference?: string;
  detail?: string;
}

/**
 * How far a new Gateway has got.
 *
 * CONNECTED means an authenticated session is established RIGHT NOW — never
 * that the enrolment token was redeemed. The two are days apart in meaning when
 * a firewall is in the way.
 */
export interface GatewayOnboarding {
  state:
    | 'WAITING_FOR_GATEWAY'
    | 'CONNECTING'
    | 'CONNECTED'
    | 'TOKEN_EXPIRED'
    | 'TOKEN_REVOKED';
  token_id: string;
  expires_at: string;
  gateway_id?: string;
  gateway_name?: string;
  enrolled_at?: string;
  agent_version?: string;
  detail?: string;
}

/**
 * One port's state, in Meshvara's vocabulary rather than a vendor's.
 *
 * `UNKNOWN` is a real value, not a missing one: a caller must be able to tell
 * "enabled" from "the driver could not tell", and an omitted field looks like
 * `false` to every UI ever written.
 */
export interface PortState {
  port: number;
  admin_state: 'ENABLED' | 'DISABLED' | 'TESTING' | 'UNKNOWN';
  link_state: 'UP' | 'DOWN' | 'TESTING' | 'DORMANT' | 'NOT_PRESENT' | 'LOWER_LAYER_DOWN' | 'UNKNOWN';
  /** The device's own identifier, e.g. `eth1/0/5`. Diagnostic only. */
  vendor_interface?: string;
}

/** What reading one port returns, outcome included. */
export interface PortStatusResult {
  outcome: 'SUCCESS' | 'FAILED' | 'UNKNOWN' | string;
  detail?: string;
  ssh_state?: string;
  port: number;
  state: PortState;
}

export const api = {
  /**
   * Sign in.
   *
   * # WHY THERE IS NO ORGANIZATION ARGUMENT
   *
   * There was one until 0.2.1, and it was an internal UUID an operator had to
   * know before they could use the product. ADR-005 fixes one Organization per
   * installation, so the Server already knows it and now resolves it itself.
   *
   * Removed from the SIGNATURE rather than defaulted, so a caller that still
   * passes one fails to compile instead of silently sending a tenant selector
   * the Server would ignore.
   */
  login: (email: string, password: string) =>
    request<LoginResult>('/api/v1/auth/login', {
      method: 'POST',
      body: { email, password },
    }),

  logout: () => request<void>('/api/v1/auth/logout', { method: 'POST' }),

  /**
   * The current session, including its CSRF token.
   *
   * Called on start-up so a RELOADED page recovers the token it holds only in
   * memory. Without it, the first write after any refresh would fail its CSRF
   * check and the user would see an inexplicable error on a form that worked a
   * moment earlier.
   */
  session: () => request<SessionInfo>('/api/v1/auth/session'),

  organization: () => request<Organization>('/api/v1/organization'),

  companies: () => request<List<Company>>('/api/v1/companies'),

  /** Sites, optionally narrowed to a company. The tenant always comes from the
   *  session — this parameter can only narrow, never reach another tenant. */
  sites: (companyId?: string) =>
    request<List<Site>>(companyId ? `/api/v1/sites?company_id=${encodeURIComponent(companyId)}` : '/api/v1/sites'),

  createCompany: (name: string) =>
    request<Company>('/api/v1/companies', { method: 'POST', body: { name } }),

  createSite: (companyId: string, name: string) =>
    request<Site>('/api/v1/sites', { method: 'POST', body: { company_id: companyId, name } }),

  /** Enrolment-token metadata for a site. Never carries a plaintext token. */
  enrolmentTokens: (siteId: string) =>
    request<{ tokens: EnrolmentToken[] }>(
      `/api/v1/sites/${encodeURIComponent(siteId)}/enrolment-tokens`,
    ),

  /**
   * Mint a one-time enrolment token for a site.
   *
   * The response carries the plaintext, once. It is deliberately NOT cached,
   * NOT retried automatically, and the caller must not persist it: a retry that
   * succeeded twice would mint two live secrets, and only one of them would be
   * on screen.
   */
  issueEnrolmentToken: (siteId: string, companyId: string, gatewayName?: string) =>
    request<IssuedEnrolmentToken>(
      `/api/v1/sites/${encodeURIComponent(siteId)}/enrolment-tokens`,
      { method: 'POST', body: { company_id: companyId, gateway_name: gatewayName ?? '' } },
    ),

  /**
   * Enrolled Gateways, optionally narrowed to a company.
   *
   * Names and states only. A Gateway's public key is identity material with no
   * use in a picker, and the endpoint does not carry it.
   */
  gateways: (companyId?: string) =>
    request<List<GatewaySummary>>(
      companyId
        ? `/api/v1/gateways?company_id=${encodeURIComponent(companyId)}`
        : '/api/v1/gateways',
    ),

  /**
   * The Server's own health and version.
   *
   * Unauthenticated on purpose — it is what a load balancer polls — so the UI
   * can show which build it is talking to even on the login screen, before
   * anyone has a session.
   */
  health: () =>
    request<{ status: string; version: string; build_time?: string }>('/healthz'),

  /**
   * Read ONE port through the device's Gateway.
   *
   * A NUMBER goes over the wire — never `Ethernet 1/0/5`, never `eth1/0/5`, and
   * never a command. The vendor spelling lives in the driver definition on the
   * Gateway, which is the only place in Meshvara where CLI syntax exists.
   */
  devicePortStatus: (deviceId: string, port: number) =>
    request<PortStatusResult>(
      `/api/v1/devices/${encodeURIComponent(deviceId)}/port-status?port=${encodeURIComponent(String(port))}`,
    ),

  /** The exact Gateway image this Server build expects. Never `:latest`. */
  gatewayArtifact: () => request<GatewayArtifact>('/api/v1/gateway/artifact'),

  /**
   * Poll one enrolment token's progress.
   *
   * Keyed by TOKEN, not by Gateway, because when the page opens the Gateway
   * does not exist yet — the token is the only handle the operator has.
   */
  gatewayOnboarding: (tokenId: string) =>
    request<GatewayOnboarding>(
      `/api/v1/enrolment-tokens/${encodeURIComponent(tokenId)}/onboarding`,
    ),

  revokeEnrolmentToken: (tokenId: string) =>
    request<void>(`/api/v1/enrolment-tokens/${encodeURIComponent(tokenId)}/revoke`, {
      method: 'POST',
    }),
};
